/**
 * Pure helpers for Maintenance GET probe classification (DE maintenance probe).
 */

export const MAINTENANCE_PROBE_TIMEOUT_MS = 8000;
export const MAINTENANCE_PROBE_ATTEMPTS = 3;
/** Ms to wait before attempt index 1 and 2 (attempt 0 is immediate). */
export const MAINTENANCE_PROBE_DELAYS_MS = [750, 1500];

/** @typedef {true | false | 'banner' | null} MaintenanceCheckOutcome */

export const MAINTENANCE_OUTCOME = {
  HEALTHY: true,
  PAGE: false,
  BANNER: "banner",
  CANCELED: null,
};

/** Set when any layer observes HTTP 503 on the Maintenance probe. */
let maintenanceHttp503Observed = false;

export const markMaintenanceHttp503Observed = (source, detail = {}) => {
  maintenanceHttp503Observed = true;
  return { source, ...detail };
};

export const wasMaintenanceHttp503Observed = () =>
  maintenanceHttp503Observed;

export const resetMaintenanceHttp503Observed = () => {
  maintenanceHttp503Observed = false;
};

const PORTAL_API_HOST_MARKERS = ["portal.api."];

let portalApiLastSuccessMs = 0;

/** Successful HTTP response from portal.api (not ADS) — used to separate outage vs probe-only flake. */
export const markPortalApiRequestSuccess = () => {
  portalApiLastSuccessMs = Date.now();
};

export const isPortalApiRecentlyReachable = (windowMs = 120000) =>
  portalApiLastSuccessMs > 0 &&
  Date.now() - portalApiLastSuccessMs < windowMs;

/** @internal test helper */
export const resetPortalApiReachabilityForTests = () => {
  portalApiLastSuccessMs = 0;
};

export const isMaintenanceProbeUrl = (url) =>
  Boolean(url && /\/api\/Maintenance/i.test(String(url)));

/**
 * ELB 503 without CORS headers surfaces as ERR_NETWORK and no response in axios
 * (browser still shows 503 in the Network panel).
 */
export const isCorsMaskedMaintenanceProbeFailure = (error) =>
  Boolean(
    error &&
    !error.response &&
    (error.code === "ERR_NETWORK" ||
      String(error.message || "").includes("Network Error")) &&
    isMaintenanceProbeUrl(error.config?.url),
  );

export const isCanceledProbeError = (error) =>
  Boolean(
    error &&
    (error.name === "AbortError" ||
      error.name === "CanceledError" ||
      error.code === "ERR_CANCELED"),
  );

export const is503ProbeError = (error) => {
  const status = error && error.response && error.response.status;
  return status === 503 || status === "503";
};

/** Resolved Maintenance probe status (checkMaintenance may return a number). */
export const isMaintenanceProbeHttp503 = (status) =>
  status === 503 || status === "503";

export const normalize503Payload = (data) => {
  if (data && typeof data === "object" && !Array.isArray(data)) {
    return {
      ...data,
      maintenance: true,
      mode: "page",
      reason: "503",
    };
  }
  const text =
    typeof data === "string" && data.trim() ? data.trim() : null;
  return {
    maintenance: true,
    mode: "page",
    reason: "503",
    body:
      text ||
      "We're currently down for scheduled maintenance. Sorry for any inconvenience — we'll be back soon.",
  };
};

export const CONNECTIVITY_BANNER_BODY =
  "Having trouble reaching the portal. Some features may not load. Check your connection or try again.";

export const normalizeUnreachablePayload = () => ({
  maintenance: true,
  mode: "banner",
  reason: "unreachable",
  body: CONNECTIVITY_BANNER_BODY,
});

/** Full maintenance page (503), not connectivity banner. */
export const isMaintenancePageState = (maintenanceState) =>
  Boolean(
    maintenanceState &&
    maintenanceState.maintenance &&
    maintenanceState.mode !== "banner",
  );

export const isConnectivityBannerState = (maintenanceState) =>
  Boolean(
    maintenanceState &&
    maintenanceState.maintenance &&
    maintenanceState.mode === "banner",
  );

export const jitterMs = (baseMs) =>
  Math.floor(baseMs * (0.75 + Math.random() * 0.5));

export const sleepMs = (ms) =>
  new Promise((resolve) => {
    setTimeout(resolve, ms);
  });

/**
 * Bound how long a probe can hang. Rejects with an axios-shaped ECONNABORTED
 * error so callers classify it as transient like a real axios timeout. The
 * underlying request is not aborted; it is left to settle and be ignored.
 */
export const withProbeTimeout = (
  promise,
  timeoutMs = MAINTENANCE_PROBE_TIMEOUT_MS,
) =>
  new Promise((resolve, reject) => {
    const timer = setTimeout(() => {
      const err = new Error("Maintenance probe timeout");
      err.name = "AxiosError";
      err.code = "ECONNABORTED";
      err.isAxiosError = true;
      reject(err);
    }, timeoutMs);
    promise.then(
      (value) => {
        clearTimeout(timer);
        resolve(value);
      },
      (error) => {
        clearTimeout(timer);
        reject(error);
      },
    );
  });
